<div class="pending-assignments-container">
  @if (showHeader()) {
    <div class="pending-assignments-header">
      <h3 class="section-title">Assignments</h3>

      <div class="header-actions">
        @if (showAddAssignment()) {
          <button
            type="button"
            class="add-assignment-btn"
            [disabled]="addAssignmentDisabled()"
            (click)="addAssignmentClicked.emit()"
          >
            <mat-icon>add</mat-icon>
            <span>Add assignment</span>
          </button>
        }

        @if (showViewAllNotes()) {
          <div class="page-title-actions">
            <!-- A real button, not a clickable div, so it is reachable by keyboard. -->
            <button
              type="button"
              class="title-action"
              (click)="viewAllNotesClicked.emit()"
            >
              <mat-icon class="title-action-icon">speaker_notes</mat-icon>
              <span class="title-action-label">View all notes</span>
            </button>
          </div>
        }

        <mat-button-toggle-group
          [value]="currentView()"
          (change)="onViewChange($event.value)"
          class="view-toggle assignments-view-toggle"
        >
          <mat-button-toggle value="active">Active</mat-button-toggle>
          <mat-button-toggle value="past">Past</mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    </div>
  }

  <!-- Hidden while loading, not merely disabled: the counts would be the record
       being replaced. The text filter is already absent then — it lives inside
       the @else below — so this keeps the two controls in step. -->
  @if (showPracticeAreaSummary() && !isLoading() && practiceAreaSummary().length > 0) {
    <div
      class="practice-area-summary"
      role="group"
      aria-label="Filter assignments by practice area"
    >
      @for (item of practiceAreaSummary(); track item.key) {
        <!-- Disabled, not hidden, while the row filters are suspended: the counts
             stay readable during a consumer's add flow, but no selection can be
             armed. Same predicate the filter itself reads, so a clickable chip
             and an applied filter always agree. -->
        <button
          type="button"
          class="practice-area-summary__card"
          [class.practice-area-summary__card--selected]="
            isPracticeAreaSelected(item.key)
          "
          [attr.aria-pressed]="isPracticeAreaSelected(item.key)"
          [attr.aria-label]="practiceAreaChipLabel(item)"
          [disabled]="!practiceAreaFilterEnabled"
          (click)="togglePracticeArea(item.key)"
        >
          {{ item.description }} - <strong>{{ item.count }}</strong>
        </button>
      }
    </div>
  }

  <ng-content></ng-content>

  @if (isLoading()) {
    <div class="loading-assignments">
      <mat-spinner diameter="24"></mat-spinner>
      <span>Loading assignments...</span>
    </div>
  } @else if (visibleAssignments.length === 0) {
    <div class="no-assignments">
      <mat-icon>assignment_turned_in</mat-icon>
      <p>{{ currentEmptyMessage }}</p>
    </div>
  } @else {
    @if (filterVisible) {
      <div class="assignments-filter">
        <mat-icon class="filter-search-icon">search</mat-icon>
        <input
          type="text"
          class="filter-input"
          [placeholder]="filterPlaceholder"
          [value]="filterText"
          (input)="onFilterInput($event)"
          aria-label="Filter assignments"
          autocomplete="off"
        />
        @if (filterText) {
          <mat-icon class="filter-clear-icon" (click)="clearFilter()"
            >close</mat-icon
          >
        }
      </div>
    }
    @if (filteredAssignments.length === 0) {
      <div class="no-assignments">
        <mat-icon>search_off</mat-icon>
        <p>No assignments match your filter.</p>
        @if (hasActiveFilters) {
          <button
            type="button"
            class="clear-filters-btn"
            (click)="clearAllFilters()"
          >
            Clear filters
          </button>
        }
      </div>
    } @else {
      <!-- The paginator is a sibling of the scrolling div, not a child: inside it
           the controls would take the table's full 700px+ width and scroll out of
           the card. The border and radius live on the block so both still read as
           one panel. -->
      <div class="table-block">
        <div class="table-container">
        <table class="assignments-table">
          <thead>
            <tr>
              <th>{{ firstColumnHeader }}</th>
              @if (mode() !== "resource") {
                <th>Practice Areas</th>
              }
              @if (showRequestId()) {
                <th>Request Id</th>
              }
              <th>Assignment Type</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>{{ latestBilledEnabled() ? "Last Billed Date" : "Duration" }}</th>
              <th>Status</th>
              <!-- Task-assignments expander. Intentionally empty: the toggle is
                   unlabelled and announced via aria-label on each button. Present
                   only with the flag, in step with the cell below it — a header
                   without its cell knocks every later column out of alignment. -->
              @if (showTaskAssignments()) {
                <th class="expand-header" aria-label="Assigned tasks"></th>
              }
              <th class="actions-header"></th>
            </tr>
          </thead>
          <tbody>
            @for (
              assignment of pagedAssignments;
              track trackByRow($index, assignment)
            ) {
              <tr
                class="assignment-row"
                [class.is-expanded]="expansion.isExpanded(assignment)"
              >
                <!-- First column: Resource or Client Matter depending on mode -->
                <td>
                  @if (mode() === "resource") {
                    <!-- Resource mode → show client/matter info -->
                    @if (assignment.clientMatterNumber) {
                      <app-matter-cell
                        [matterNumber]="assignment.clientMatterNumber"
                        [matterName]="assignment.matterTitle"
                        [clientName]="assignment.clientName"
                        [link]="canViewMatter()"
                        (clicked)="onMatterClick($event)"
                      />
                    } @else {
                      <span class="no-matter">-</span>
                    }
                  } @else {
                    <!-- Matter mode → show resource info -->
                    @if (assignment.employeeId) {
                      @if (assignment.canViewResource !== false) {
                        <a
                          class="resource-cell resource-cell-link"
                          (click)="onResourceClick(assignment.employeeId!)"
                        >
                          <div class="resource-name">
                            {{ assignment.fullName }}
                          </div>
                          @if (assignment.title) {
                            <div class="resource-title">
                              {{ assignment.title }}
                            </div>
                          }
                        </a>
                      } @else {
                        <div class="resource-cell resource-cell--plain">
                          <div class="resource-name">
                            {{ assignment.fullName }}
                          </div>
                          @if (assignment.title) {
                            <div class="resource-title">
                              {{ assignment.title }}
                            </div>
                          }
                        </div>
                      }
                    } @else {
                      <span class="no-resource">Not assigned</span>
                    }
                  }
                </td>

                <!-- Practice Areas -->
                @if (mode() !== "resource") {
                  <td class="practice-areas-cell">
                    @if (
                      assignment.practiceAreas &&
                      assignment.practiceAreas.length > 0
                    ) {
                      <app-membership-chips
                        [items]="$any(assignment.practiceAreas)"
                        labelKey="description"
                      />
                    } @else {
                      <span class="text-muted">—</span>
                    }
                  </td>
                }

                <!-- Request Id -->
                @if (showRequestId()) {
                  <td class="request-id-cell">
                    @if (assignment.requestId) {
                      @if (
                        canViewRequest() && assignment.canViewRequest !== false
                      ) {
                        <a
                          class="request-id-link"
                          [routerLink]="['/requests', assignment.requestId]"
                          >{{ assignment.requestId }}</a
                        >
                      } @else {
                        {{ assignment.requestId }}
                      }
                    } @else {
                      -
                    }
                  </td>
                }

                <!-- Assignment type -->
                <td>
                  @if (assignment.assignmentTypeCategory) {
                    <span
                      class="role-badge"
                      [class.primary]="
                        assignment.assignmentTypeCategory === 'PRIMARY'
                      "
                      [class.coverage]="
                        assignment.assignmentTypeCategory === 'COVERAGE'
                      "
                      [class.system]="
                        assignment.assignmentTypeCategory === 'SYSTEM'
                      "
                    >
                      {{
                        assignment.assignmentTypeCategoryName ||
                          assignment.assignmentTypeCategory
                      }}
                    </span>
                  } @else {
                    <span class="no-role">-</span>
                  }
                </td>

                <!-- Start date (a system row's is a UTC-midnight first-billed date) -->
                <td class="date-cell">
                  @if (!assignment.startDate) {
                    -
                  } @else if (isSystemRow(assignment)) {
                    {{ formatUtcDate(assignment.startDate) }}
                  } @else {
                    {{ formatDate(assignment.startDate) }}
                  }
                </td>

                <!-- End date -->
                <td class="date-cell">
                  @if (isOverdueActive(assignment)) {
                    <span class="date-overdue">
                      {{ formatDate(assignment.endDate!) }}
                      <mat-icon
                        class="overdue-warning-icon"
                        matTooltip="The end date has passed, but this assignment is still active."
                        matTooltipClass="light-tooltip"
                        >warning</mat-icon
                      >
                    </span>
                  } @else {
                    {{
                      assignment.endDate ? formatDate(assignment.endDate) : "-"
                    }}
                  }
                </td>

                <!-- Duration, or Last Billed Date while assignments-latest-billed is on -->
                <td class="duration-cell">
                  @if (latestBilledEnabled()) {
                    @if (assignment.lastBilledDate) {
                      {{ formatUtcDate(assignment.lastBilledDate) }}
                    } @else {
                      -
                    }
                  } @else if (getAssignmentLengthInWeeks(assignment); as weeks) {
                    {{ weeks }} wk{{ weeks > 1 ? "s" : "" }}
                  } @else {
                    -
                  }
                </td>

                <!-- Status -->
                <td>
                  <span
                    class="status-badge"
                    [ngClass]="getStatusClass(assignment.status)"
                  >
                    {{ assignment.statusName || assignment.status }}
                  </span>
                </td>

                <!-- Assigned tasks expander. Every row can open, including one with
                     no tasks: the panel then says so, which is a different answer
                     from a row nobody has looked at. -->
                @if (showTaskAssignments()) {
                  <td class="expand-cell">
                    <button
                      type="button"
                      class="expand-toggle"
                      [attr.aria-expanded]="expansion.isExpanded(assignment)"
                      [attr.aria-controls]="expansion.panelId(assignment)"
                      [attr.aria-label]="
                        expansion.isExpanded(assignment)
                          ? 'Hide assigned tasks'
                          : 'Show assigned tasks'
                      "
                      (click)="expansion.toggle(assignment)"
                    >
                      @if (expansion.label(assignment)) {
                        <span class="task-count">{{
                          expansion.label(assignment)
                        }}</span>
                      }
                      <mat-icon
                        class="expand-icon"
                        [class.is-expanded]="expansion.isExpanded(assignment)"
                        >chevron_right</mat-icon
                      >
                    </button>
                  </td>
                }

                <!-- Actions -->
                <td class="actions-cell">
                  <!--
                  The single UI gate for pending request-sourced assignments: they
                  get a lock instead of the ellipsis, so they never reach the edit
                  dialog or the row menu. Mirrors the server's own gate — the
                  assignment service refuses every status, date and type write to
                  such a row with a 409 — so this is the courtesy, not the lock.
                -->
                  @if (
                    assignment.assignedResourceId &&
                    assignment.requestId &&
                    assignment.status.toUpperCase() === "PENDING"
                  ) {
                    <span
                      [matTooltip]="
                        'Linked to Request #' +
                        assignment.requestId +
                        ' — fulfill the request to edit this assignment.'
                      "
                      matTooltipClass="light-tooltip"
                    >
                      <button
                        type="button"
                        mat-icon-button
                        disableRipple
                        disabled
                        class="locked-icon-button"
                      >
                        <mat-icon>lock</mat-icon>
                      </button>
                    </span>
                  } @else if (offersRowMenu(assignment)) {
                    <!-- The row menu: for editors on a real row, with the dialog as
                         one of its items; for creators on a synthetic row, whose items
                         create the assignment. While a write for this row is in
                         flight it is disabled, so it cannot be fired twice, and the
                         ellipsis gives way to a spinner. -->
                    @let updating = isStatusUpdating(assignment);
                    <button
                      type="button"
                      mat-icon-button
                      disableRipple
                      class="ellipsis-icon-button"
                      [class.has-notes]="rowHasAnyNote(assignment)"
                      [class.is-updating]="updating"
                      [disabled]="updating"
                      [attr.aria-busy]="updating ? 'true' : null"
                      [attr.aria-label]="
                        updating ? 'Updating assignment…' : 'Assignment actions'
                      "
                      matTooltip="Assignment actions"
                      matTooltipClass="light-tooltip"
                      [matMenuTriggerFor]="rowMenu"
                      [matMenuTriggerData]="{ row: assignment }"
                    >
                      @if (updating) {
                        <mat-spinner diameter="20" aria-hidden="true"></mat-spinner>
                      } @else {
                        <mat-icon>more_vert</mat-icon>
                      }
                    </button>
                  } @else if (assignment.assignedResourceId) {
                    <!-- Without Assignments.Edit the menu would hold nothing but
                         "View assignment", so the ellipsis opens the read-only
                         dialog directly. A synthetic row has nothing to view and
                         renders no action at all for a viewer who cannot create. -->
                    <button
                      type="button"
                      mat-icon-button
                      disableRipple
                      class="ellipsis-icon-button"
                      [class.has-notes]="rowHasAnyNote(assignment)"
                      aria-label="View assignment"
                      matTooltip="View assignment"
                      matTooltipClass="light-tooltip"
                      (click)="openEditDialog(assignment)"
                    >
                      <mat-icon>more_vert</mat-icon>
                    </button>
                  }
                </td>
              </tr>

              <!-- Detail row beneath each expandable row. Spans every column, and is
                   rendered collapsed rather than omitted: the toggle's aria-controls
                   has to point at an element that exists. Collapsed it holds no
                   content and `height: 0` keeps it out of the layout. -->
              @if (showTaskAssignments()) {
                <tr class="task-assignments-row">
                  <td
                    class="task-assignments-cell"
                    [attr.colspan]="columnCount()"
                    [id]="expansion.panelId(assignment)"
                    [attr.aria-hidden]="
                      expansion.isExpanded(assignment) ? null : 'true'
                    "
                  >
                    @if (expansion.isExpanded(assignment)) {
                      <app-task-assignment-list
                        [tasks]="assignment.taskAssignments ?? []"
                        animate.enter="is-entering"
                        animate.leave="is-leaving"
                      />
                    }
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
        </div>
        @if (paginatorVisible) {
          <mat-paginator
            [length]="filteredAssignments.length"
            [pageSize]="pageSize"
            [pageIndex]="clampedPageIndex"
            [pageSizeOptions]="pageSizeOptions"
            showFirstLastButtons
            aria-label="Select page of assignments"
            (page)="onPageChange($event)"
          ></mat-paginator>
        }
      </div>
    }
  }

  <!--
    One menu for every row, rendered lazily into the overlay with the clicked row as
    its context (matMenuTriggerData). Declared at the template root, outside every
    control-flow block, so the trigger inside the table can reference it. Opens
    toward the left: the actions cell is pinned to the card's right edge.
  -->
  <mat-menu #rowMenu="matMenu" class="assignment-row-menu" xPosition="before">
    <ng-template matMenuContent let-row="row">
      <!-- A synthetic row is not an assignment yet, so there is nothing to edit:
           its items are the three statuses, each of which creates it. -->
      @if (!isSystemRow(row)) {
        <button
          type="button"
          mat-menu-item
          class="assignment-row-menu__item"
          (click)="openEditDialog(row)"
        >
          <mat-icon>edit</mat-icon>
          <span>Edit assignment</span>
        </button>
      }
      @for (
        option of quickStatusOptions(row);
        track option.value;
        let first = $first
      ) {
        <button
          type="button"
          mat-menu-item
          class="assignment-row-menu__item"
          [class.assignment-row-menu__item--group-start]="
            first && !isSystemRow(row)
          "
          (click)="markStatus(row, option)"
        >
          <span class="assignment-row-menu__status" aria-hidden="true">
            <span
              class="assignment-row-menu__status-dot"
              [ngClass]="
                'assignment-row-menu__status-dot--' + option.value.toLowerCase()
              "
            ></span>
          </span>
          <span>Mark {{ option.label }}</span>
        </button>
      }
    </ng-template>
  </mat-menu>
</div>
